export const createRadReceivingTourSteps = (
  searchTerm: string,
  selectedHeader: any,
  lines: any[],
  scanFields: any,
  setCurrentStep: (step: number | ((prev: number) => number)) => void,
  isDarkTheme?: boolean
) => {
  return [
    {
      selector: 'body',
      content: 'Welcome to the RMA Receive & Equipment Return Dashboard! This tour will guide you through the complete process of receiving and processing repair returns.',
      styles: {
        popover: (base: any) => ({
          ...base,
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: '500px',
          backgroundColor: isDarkTheme ? '#2a2a2a' : '#ffffff',
          color: isDarkTheme ? '#ffffff' : '#000000',
        }),
      },
      action: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
    },
    {
      selector: '[data-tour="search-input"]',
      content: searchTerm ? 'Start by entering a search term here - you can search by Request Number, Tracking Number, or Serial Number.' : '⚠️ Please enter a search term to proceed. You can search by Request Number, Tracking Number, or Serial Number.',
      action: () => {
        const element = document.querySelector('[data-tour="search-input"]');
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    },
    {
      selector: '[data-tour="include-closed"]',
      content: 'Optionally, toggle this checkbox if you want to Include Received Orders in your search results.',
      action: () => {
        const element = document.querySelector('[data-tour="include-closed"]');
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    },
    {
      selector: '[data-tour="search-button"]',
      content: 'Click the Search button to find matching orders.',
      action: () => {
        const element = document.querySelector('[data-tour="search-button"]');
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    },
    {
      selector: '[data-tour="header-table"]',
      content: searchTerm ? 'Your search results appear here. Click on any header row to view its line details.' : '⚠️ After searching, results will appear here. Click on any header row to view its line details.',
      action: () => {
        const element = document.querySelector('[data-tour="header-table"]');
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    },
    {
      selector: '[data-tour="to-site-cell"]',
      content: 'If you need to transfer items to a different site, you can change the "To Site" value in the dropdown for any header row.',
      action: () => {
        const element = document.querySelector('[data-tour="to-site-cell"]');
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    },
    {
      selector: '[data-tour="pallet-field"]',
      content: selectedHeader ? 'Enter the Pallet number here. You can scan or type it manually.' : '⚠️ After selecting a header, you can enter the Pallet number here.',
      action: () => {
        const element = document.querySelector('[data-tour="pallet-field"]');
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    },
    {
      selector: '[data-tour="subinventory-field"]',
      content: selectedHeader ? 'Once pallet is selected, subinventory will be automatically populated.' : '⚠️ After selecting a header and pallet, subinventory will auto-populate.',
      action: () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
    },
    {
      selector: '[data-tour="cage-field"]',
      content: selectedHeader ? 'Enter the Cage number or click Skip to use default value.' : '⚠️ After selecting a header, you can enter the Cage number or skip.',
      action: () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
    },
    {
      selector: '[data-tour="tote-field"]',
      content: selectedHeader ? 'Enter the Tote number or click Skip to use default value.' : '⚠️ After selecting a header, you can enter the Tote number or skip.',
      action: () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
    },
    {
      selector: '[data-tour="tracking-field"]',
      content: selectedHeader ? 'Enter the tracking number for this shipment. This field is mandatory.' : '⚠️ After selecting a header, enter the tracking number (mandatory).',
      action: () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
    },
    {
      selector: '[data-tour="serial-field"]',
      content: selectedHeader ? 'Scan or enter the serial number of the returned item here. The system will validate it against the lines.' : '⚠️ After selecting a header, scan or enter serial numbers here.',
      action: () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
    },
    {
      selector: '[data-tour="lines-table"]',
      content: selectedHeader ? 'This table shows all line items for the selected return request. Validated items will move to the top.' : '⚠️ After selecting a header, line items will appear here. Validated items move to the top.',
      action: () => {
        const element = document.querySelector('[data-tour="lines-table"]');
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    },
    {
      selector: '[data-tour="action-buttons"]',
      content: selectedHeader ? 'Use these buttons to manage your changes.' : '⚠️ After selecting a header, use these buttons to manage your changes.',
      action: () => {
        const element = document.querySelector('[data-tour="action-buttons"]');
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    },
    {
      selector: '[data-tour="cancel-button"]',
      content: 'Cancel will discard all unsaved changes and revert to the original state.',
      action: () => {
        const element = document.querySelector('[data-tour="cancel-button"]');
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    },
    {
      selector: '[data-tour="save-button"]',
      content: 'Save will store your changes as a draft without submitting the request.',
      action: () => {
        const element = document.querySelector('[data-tour="save-button"]');
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    },
    {
      selector: '[data-tour="submit-button"]',
      content: 'Submit will finalize and submit the return request for processing.',
      action: () => {
        const element = document.querySelector('[data-tour="submit-button"]');
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    },
    {
      selector: 'body',
      content: 'This completes the tour! You now know how to process repair returns. Click X to exit.',
      styles: {
        popover: (base: any) => ({
          ...base,
          backgroundColor: isDarkTheme ? '#2a2a2a' : '#ffffff',
          color: isDarkTheme ? '#ffffff' : '#000000',
        }),
      },
      action: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
    },
  ];
};
